import styled, { css, keyframes } from 'styled-components';
import { CHART_ANIMATION_CONFIG } from '@mews/b2b-ui';
import { getToken, Stack } from '@optimus-web/core';

const fillSegment = (circumference: number, segmentLength: number) => keyframes`
    from {
        stroke-dasharray: 0, ${circumference};
    }
    to {
        stroke-dasharray: ${segmentLength}, ${circumference - segmentLength};
    }
`;

const rotateCircle = (segmentLength: number, strokeDashoffset: number, segmentGapSize: number) => keyframes`
    from {
        opacity: 0;
        stroke-dashoffset: ${strokeDashoffset - segmentGapSize};
        }
    to {
        opacity: 1;
        stroke-dashoffset: ${strokeDashoffset - segmentLength};
    }
`;

export const CircularContainer = styled.svg`
    display: block;
`;

const BaseSegment = styled.circle<{
    animated: boolean;
    segmentLength: number;
    circumference: number;
    color: string;
    strokeDashoffset: number;
    segmentGapSize: number;
}>`
    fill: none;
    stroke: ${({ color }) => color};
    stroke-linecap: butt;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
`;

export const Segment = styled(BaseSegment)<{
    strokeWidth: number;
}>`
    stroke-width: ${({ strokeWidth }) => strokeWidth};
    stroke-dasharray: ${({ circumference }) => `0, ${circumference}`};
    stroke-dashoffset: ${({ strokeDashoffset }) => strokeDashoffset};

    ${({ animated, circumference, strokeDashoffset, segmentLength }) =>
        animated
            ? css`
                  animation-name: ${fillSegment(circumference, segmentLength)};
                  animation-duration: ${CHART_ANIMATION_CONFIG.animationDuration}ms;
                  animation-timing-function: ${CHART_ANIMATION_CONFIG.animationEasing};
                  animation-delay: ${CHART_ANIMATION_CONFIG.animationBegin}ms;
                  animation-iteration-count: 1;
                  animation-fill-mode: forwards;
              `
            : css`
                  stroke-dashoffset: ${strokeDashoffset};
                  stroke-dasharray: ${segmentLength}, ${circumference - segmentLength};
              `}
`;

export const LastSegmentRoundEnding = styled(BaseSegment)`
    opacity: 0;
    stroke-linecap: round;
    stroke-dasharray: ${({ circumference }) => `0, ${circumference}`};
    stroke-dashoffset: ${({ strokeDashoffset, segmentGapSize }) => strokeDashoffset - segmentGapSize};

    ${({ animated, circumference, strokeDashoffset, segmentLength, segmentGapSize }) =>
        animated
            ? css`
                  animation-name: ${rotateCircle(segmentLength, strokeDashoffset, segmentGapSize)};
                  animation-duration: ${CHART_ANIMATION_CONFIG.animationDuration}ms;
                  animation-timing-function: ${CHART_ANIMATION_CONFIG.animationEasing};
                  animation-delay: ${CHART_ANIMATION_CONFIG.animationBegin}ms;
                  animation-iteration-count: 1;
                  animation-fill-mode: forwards;
              `
            : css`
                  stroke-dashoffset: ${strokeDashoffset - segmentLength};
                  stroke-dasharray: 0, ${circumference};
                  opacity: 1;
              `}
`;

export const BackgroundCircle = styled.circle`
    fill: none;
    stroke: ${getToken('backgroundInteractiveNeutralDefault')};
`;

export const ProgressChartContent = styled(Stack)<{ show?: boolean; animated?: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    ${({ animated }) =>
        animated &&
        css`
            transition: opacity 0.2s ease-out;
            opacity: 0;
            width: 0;
            visibility: hidden;
        `}

    ${({ show }) =>
        show &&
        css`
            opacity: 1;
            width: auto;
            visibility: visible;
        `}
`;
